import { ROUTES } from '@app/constants';
import {
  CancelCreatorForm,
  ChangeCommercialsForm,
  ChangeDeliverablesForm,
  ReimbursementForm
} from '@entities/campaigns/ui';
import useApproveAlteration from '@entities/financeApproval/lib/hooks/useApproveAlteration';
import useRejectAlteration from '@entities/financeApproval/lib/hooks/useRejectAlteration';
import { ToastService } from '@shared/lib/services';
import { Modal } from '@shared/ui';
import { Button } from '@shared/ui/Button';
import { Flex, Input, Popover } from 'antd';
import { Ban, Package, Receipt, TrendingUp } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { ICampaignAlterationApprovalRequest } from '../../entities/campaigns/lib/types';
import styles from './styles.module.scss';

// ==========================================
// Type Definitions
// ==========================================
export type AdminDetailsChangeType =
  | 'reimbursement'
  | 'change_commercials'
  | 'change_deliverables_commercials'
  | 'cancel_creator';

export interface ProofAttachment {
  id: string;
  name: string;
  url?: string;
  file?: File;
}

export interface AdminDetailsChangeSubmitPayload {
  type: AdminDetailsChangeType;
  summaryText: string;
  newInternal: number | null;
  newBrand: number | null;
  newDeliverables: string[];
  changedDeliverableLabels: string[];
  proofAttachments?: ProofAttachment[];
  cancelReason?: string;
}

export interface AdminCampaignDetailsChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  creatorName?: string;
  baseInfluencerCommercial?: number | null;
  baseBrandCommercial?: number | null;
  currentDeliverables?: string[];
  briefId?: string;
  campaignInfluencerId?: string;
  onSubmit?: (payload: AdminDetailsChangeSubmitPayload) => void;
  viewOnly?: boolean;
  alterationRequest?: ICampaignAlterationApprovalRequest;
}

export const ADMIN_DETAILS_CHANGE_OPTIONS: Array<{
  id: AdminDetailsChangeType;
  title: string;
  description: string;
  tone?: 'danger';
}> = [
  {
    id: 'reimbursement',
    title: 'Reimbursement',
    description: 'Add a reimbursement for influencer expenses (no GST/TDS ledger impact).',
  },
  {
    id: 'change_commercials',
    title: 'Change Commercials',
    description: 'Increase or decrease influencer or brand commercials.',
  },
  {
    id: 'change_deliverables_commercials',
    title: 'Change Deliverables & Commercials',
    description: 'Update deliverable scope along with commercial adjustments.',
  },
  {
    id: 'cancel_creator',
    title: 'Cancel Creator',
    description: 'Mark creator as cancelled and halt campaign activities.',
    tone: 'danger',
  },
];

const AdminCampaignDetailsChangeModal: React.FC<AdminCampaignDetailsChangeModalProps> = ({
  isOpen,
  onClose,
  creatorName = '',
  baseInfluencerCommercial = null,
  baseBrandCommercial = null,
  currentDeliverables = [],
  briefId = '',
  campaignInfluencerId = '',
  onSubmit,
  viewOnly = false,
  alterationRequest,
}) => {
  const [step, setStep] = useState<'choose' | 'form'>('choose');
  const [selectedType, setSelectedType] = useState<AdminDetailsChangeType | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectPopoverOpen, setRejectPopoverOpen] = useState(false);

  const {
    approveAlteration,
    isApproveAlterationLoading,
    isApproveAlterationSuccess,
    approveAlterationError
  } = useApproveAlteration(briefId);
  const {
    rejectAlteration,
    isRejectAlterationLoading,
    isRejectAlterationSuccess,
    rejectAlterationError
  } = useRejectAlteration();

  // Reset form states when modal opens/closes
  useEffect(() => {
    if (!isOpen) return;
    if (alterationRequest) {
      setStep('form');
      const mappedType =
        alterationRequest.type === "Reimbursement"
          ? "reimbursement"
          : alterationRequest.type === "Cost-Adjustment"
          ? "change_commercials"
          : alterationRequest.type.includes("Cancellation")
          ? "cancel_creator"
          : "change_deliverables_commercials";
      setSelectedType(mappedType);
    } else {
      setStep('choose');
      setSelectedType(null);
    }
  }, [isOpen, alterationRequest]);

  useEffect(() => {
    if (isApproveAlterationSuccess) {
      ToastService.success("Alteration approved successfully.");
      onClose?.();
    }
    // else if (approveAlterationError) {
    //   ToastService.error(approveAlterationError.message);
    // }
  }, [isApproveAlterationSuccess]);

  useEffect(() => {
    if (isRejectAlterationSuccess) {
      ToastService.success("Alteration rejected.");
      onClose?.();
    }
    // else if (rejectAlterationError) {
    //   ToastService.error(rejectAlterationError.message);
    // }
  }, [isRejectAlterationSuccess]);

  const handleChoose = (type: AdminDetailsChangeType) => {
    setSelectedType(type);
    setStep('form');
  };

  const handleBack = () => {
    setStep('choose');
    setSelectedType(null);
  };

  const optionIcon = (id: AdminDetailsChangeType) => {
    switch (id) {
      case 'reimbursement':
        return <Receipt size={18} style={{ color: '#635bff' }} />;
      case 'change_commercials':
        return <TrendingUp size={18} style={{ color: '#635bff' }} />;
      case 'change_deliverables_commercials':
        return <Package size={18} style={{ color: '#635bff' }} />;
      case 'cancel_creator':
        return <Ban size={18} style={{ color: '#e11d48' }} />;
    }
  };

  const selectedOption = ADMIN_DETAILS_CHANGE_OPTIONS.find((o) => o.id === selectedType);
  const modalTitle =
    step === 'choose'
      ? 'Change Campaign Details'
      : viewOnly ? "Alteration Request" : selectedOption?.title ?? 'Change Campaign Details';

  const cName = creatorName || (alterationRequest ? (alterationRequest.influencerName || alterationRequest.socialMediaName || '') : '');
  const bId = briefId || (alterationRequest ? alterationRequest.briefId : '');
  const cInfId = campaignInfluencerId || (alterationRequest ? alterationRequest.campaignInfluencerId : '');

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      title={modalTitle}
      className={styles.modal}
      footer={
        step === 'choose' && !viewOnly ? (
          <div className={styles.footer}>
            <button type="button" onClick={onClose} className={styles.cancelBtn}>
              Cancel
            </button>
          </div>
        ) : viewOnly && alterationRequest?.status === "Pending" ? (
          <Flex justify='flex-end' gap={10}>
            <Button loading={isApproveAlterationLoading} type='primary' onClick={() => {
              approveAlteration(alterationRequest?._id || "");
            }}>Approve</Button>
            <Popover
              content={
                <div style={{ width: 300 }}>
                  <Input.TextArea
                    rows={3}
                    placeholder="Reason for rejection"
                    value={rejectReason}
                    onChange={e => setRejectReason(e.target.value)}
                  />
                  <Flex gap={8} style={{ marginTop: 8, justifyContent: 'flex-end' }}>
                    <Button size="small" onClick={() => { setRejectPopoverOpen(false); setRejectReason(''); }}>
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      danger
                      loading={isRejectAlterationLoading}
                      onClick={() => {
                        rejectAlteration({ alterationId: alterationRequest?._id || "", rejectReason });
                        setRejectPopoverOpen(false);
                      }}
                    >
                      Reject
                    </Button>
                  </Flex>
                </div>
              }
              trigger="click"
              open={rejectPopoverOpen}
              onOpenChange={setRejectPopoverOpen}
            >
              <Button danger>Reject</Button>
            </Popover>
          </Flex>
        ) : null
      }
      width={720}
      destroyOnClose
    >
      {!viewOnly && (
        <p className={styles.modalDescription}>
          {step === 'choose'
            ? `Choose what you want to update for ${cName}.`
            : selectedOption?.description}
        </p>
      )}

      {viewOnly && alterationRequest && (
        <div className={styles.campaignBrandHeader} style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 4, paddingBottom: 12, borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ display: 'flex', gap: 8, fontSize: '13px', alignItems: "center" }}>
            <span style={{ fontWeight: 500, color: '#6b7280', minWidth: '110px' }}>Campaign:</span>
            <Button type='link' size='small' style={{padding: 0, margin: 0}} className={styles.advancedTaxToggle} onClick={() => {
              window.open(ROUTES.BRIEF_V2.replace(":briefId", alterationRequest?.briefId));
            }}>{alterationRequest.briefName || alterationRequest.brief?.name || '—'}</Button>
          </div>
          <div style={{ display: 'flex', gap: 8, fontSize: '13px' }}>
            <span style={{ fontWeight: 500, color: '#6b7280', minWidth: '110px' }}>Brand:</span>
            <span style={{ fontWeight: 600, color: '#1a1f36' }}>{alterationRequest.brand?.name || '—'}</span>
          </div>
          <div style={{ display: 'flex', gap: 8, fontSize: '13px' }}>
            <span style={{ fontWeight: 500, color: '#6b7280', minWidth: '110px' }}>Creator:</span>
            <span style={{ fontWeight: 600, color: '#1a1f36' }}>{alterationRequest.socialMediaName}</span>
          </div>
        </div>
      )}

      {/* Step 1: Option Selection */}
      {step === 'choose' && !viewOnly && (
        <div className={styles.optionList}>
          {ADMIN_DETAILS_CHANGE_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => handleChoose(option.id)}
              className={`${styles.optionCard} ${option.tone === 'danger' ? styles.danger : ''}`}
            >
              <div className={styles.cardContent}>
                <div className={styles.iconContainer}>
                  {optionIcon(option.id)}
                </div>
                <div>
                  <p className={styles.cardTitle}>{option.title}</p>
                  <p className={styles.cardDesc}>{option.description}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Step 2: Form components from Entities folder */}
      {step === 'form' && selectedType === 'reimbursement' && (
        <ReimbursementForm
          creatorName={cName}
          briefInfluencerId={cInfId}
          campaignInfluencerId={cInfId}
          briefId={bId}
          onCancel={handleBack}
          onSuccess={onClose}
          viewOnly={viewOnly}
          alterationRequest={alterationRequest}
        />
      )}

      {step === 'form' && selectedType === 'change_commercials' && (
        <ChangeCommercialsForm
          creatorName={cName}
          campaignInfluencerId={cInfId}
          briefId={bId}
          onCancel={handleBack}
          onSuccess={onClose}
          viewOnly={viewOnly}
          alterationRequest={alterationRequest}
        />
      )}

      {step === 'form' && selectedType === 'change_deliverables_commercials' && (
        <ChangeDeliverablesForm
          creatorName={cName}
          campaignInfluencerId={cInfId}
          briefId={bId}
          onCancel={handleBack}
          onSuccess={onClose}
          viewOnly={viewOnly}
          alterationRequest={alterationRequest}
        />
      )}

      {step === 'form' && selectedType === 'cancel_creator' && (
        <CancelCreatorForm
          creatorName={cName}
          campaignInfluencerId={cInfId}
          briefId={bId}
          onCancel={handleBack}
          onSuccess={onClose}
          viewOnly={viewOnly}
          alterationRequest={alterationRequest}
        />
      )}
    </Modal>
  );
};

export default AdminCampaignDetailsChangeModal;
